import type { AxiosInstance } from "axios";
import { Download, FileDown } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { apiErrorMessage } from "../lib/api.js";
import {
  fetchDownloadUrl,
  fetchExportList,
  triggerBrowserDownload,
  type ExportJobSummary,
} from "../lib/exports.js";
import { findReport } from "../lib/reports.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Progress } from "../components/ui/progress.js";
import { Skeleton } from "../components/ui/skeleton.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table.js";
import { useTableResize } from "../components/TableResizeHandle.js";

/**
 * Every export this store has requested: who generated it, over which dates, and the
 * file. The row's description comes from the `filters` json the API stored at request
 * time — the row describes the file that WAS generated, even if a report's defaults
 * change later.
 *
 * Download always fetches a FRESH signed link; a row from last week works exactly like
 * one from a minute ago, which is why there is no "expired" state on this screen.
 */

const PAGE = 25;
const POLL_MS = 5_000;

function reportLabel(reportType: string): string {
  return findReport(reportType)?.label ?? reportType;
}

function filterText(filters: Readonly<Record<string, unknown>>): string {
  const from = typeof filters["from"] === "string" ? filters["from"] : "?";
  const to = typeof filters["to"] === "string" ? filters["to"] : "?";
  return `${from} → ${to}`;
}

function formatOf(filters: Readonly<Record<string, unknown>>): string {
  return typeof filters["format"] === "string" ? filters["format"] : "xlsx";
}

function generatedAt(createdAt: string): string {
  const at = new Date(createdAt);
  return Number.isNaN(at.getTime()) ? createdAt : at.toLocaleString();
}

export function MyExportsScreen({
  client,
  tenantId,
}: {
  readonly client: AxiosInstance;
  /** Only the reason to refetch — the client sends it as X-Tenant-Id itself. */
  readonly tenantId: string;
}): React.JSX.Element {
  const [items, setItems] = useState<readonly ExportJobSummary[] | null>(null);
  const { colWidths, startResize } = useTableResize([180, 180, 100, 150, 150, 100, 120, 90]);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Same out-of-order guard as ReportScreen: only the newest request may write state.
  const ticket = useRef(0);

  const load = useCallback(async () => {
    const mine = ++ticket.current;
    try {
      const rows = await fetchExportList(client, { limit: PAGE, offset });
      if (mine !== ticket.current) return;
      setItems(rows);
      setError(null);
    } catch (caught) {
      if (mine !== ticket.current) return;
      setError(apiErrorMessage(caught, "Could not load your exports."));
    }
  }, [client, offset]);

  useEffect(() => {
    setItems(null);
    void load();
  }, [load, tenantId]);

  // Keep the visible page honest while anything on it is still generating.
  const running = items?.some((item) => item.status === "queued" || item.status === "processing") ?? false;
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [running, load]);

  const download = (id: string): void => {
    void fetchDownloadUrl(client, id)
      .then(triggerBrowserDownload)
      .catch((caught: unknown) => {
        setError(apiErrorMessage(caught, "Could not fetch a download link."));
      });
  };

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <header>
        <h1 className="flex items-center gap-2.5 text-2xl font-bold tracking-tight">
          <FileDown className="text-primary size-6" />
          My Exports
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Files exported from your reports. Links are signed freshly on every download, so old rows keep working.
        </p>
      </header>

      {error !== null && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}

      {items === null ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : items.length === 0 && offset === 0 ? (
        <p className="text-muted-foreground text-sm">
          Nothing exported yet. Open a report and use its Export button — the file will appear here.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border">
          <Table className="table-fixed">
            <colgroup>
              {colWidths.map((w, i) => (
                <col key={i} style={{ width: `${w}px` }} />
              ))}
            </colgroup>
            <TableHeader>
              <TableRow>
                <TableHead onResize={(e) => startResize(0, e)}>Report</TableHead>
                <TableHead onResize={(e) => startResize(1, e)}>Date range</TableHead>
                <TableHead onResize={(e) => startResize(2, e)}>Format</TableHead>
                <TableHead onResize={(e) => startResize(3, e)}>Generated at</TableHead>
                <TableHead onResize={(e) => startResize(4, e)}>Generated by</TableHead>
                <TableHead className="text-right" onResize={(e) => startResize(5, e)}>Rows</TableHead>
                <TableHead onResize={(e) => startResize(6, e)}>Status</TableHead>
                <TableHead onResize={(e) => startResize(7, e)} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <ExportRow key={item.id} item={item} onDownload={download} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {items !== null && (items.length === PAGE || offset > 0) && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0}
            onClick={() => {
              setOffset((current) => Math.max(0, current - PAGE));
            }}
          >
            Newer
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={items.length < PAGE}
            onClick={() => {
              setOffset((current) => current + PAGE);
            }}
          >
            Older
          </Button>
        </div>
      )}
    </section>
  );
}

function ExportRow({
  item,
  onDownload,
}: {
  readonly item: ExportJobSummary;
  readonly onDownload: (id: string) => void;
}): React.JSX.Element {
  const truncated = item.status === "ready" && item.error !== null;
  return (
    <TableRow>
      <TableCell className="font-medium">{reportLabel(item.reportType)}</TableCell>
      <TableCell className="whitespace-nowrap">{filterText(item.filters)}</TableCell>
      <TableCell>
        <Badge variant="outline" className="uppercase">
          {formatOf(item.filters)}
        </Badge>
      </TableCell>
      <TableCell className="whitespace-nowrap">{generatedAt(item.createdAt)}</TableCell>
      <TableCell>{item.requestedBy ?? "—"}</TableCell>
      <TableCell className="text-right tabular-nums">{item.rowCount ?? "—"}</TableCell>
      <TableCell>
        {item.status === "ready" && !truncated && <Badge variant="secondary">Ready</Badge>}
        {item.status === "ready" && truncated && (
          // A truncated file says so wherever the file is offered — same rule as the tray.
          <Badge variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-400">
            Ready (row limit)
          </Badge>
        )}
        {item.status === "failed" && (
          <span title={item.error ?? undefined}>
            <Badge variant="destructive">Failed</Badge>
          </span>
        )}
        {(item.status === "queued" || item.status === "processing") && (
          <div className="flex w-28 items-center gap-2">
            <Progress value={item.status === "queued" ? 3 : item.progress} className="w-20" />
            <span className="text-muted-foreground text-xs tabular-nums">{item.progress}%</span>
          </div>
        )}
      </TableCell>
      <TableCell>
        {item.status === "ready" && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onDownload(item.id);
            }}
          >
            <Download className="size-3.5" />
            Download
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
